'use client'

import type { DeliveryMethod } from '@repo/core/delivery'
import { DEFAULT_PRICING_RULES, type PricingRuleValues } from '@repo/db/pricing-rules'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader } from '@/components/molecules/page-header'
import { PricingRulesProvider } from '@/components/providers/PricingRulesProvider'
import { type Placed, SuccessPanel } from '@/components/sections/creator/success-panel'
import { OrderCatalogue } from '@/components/sections/order/order-catalogue.client'
import { OrderContactCard } from '@/components/sections/order/order-contact-card.client'
import { OrderLineList } from '@/components/sections/order/order-line-list.client'
import { OrderTotalCard } from '@/components/sections/order/order-total-card.client'
import { useOrderLines } from '@/components/sections/order/use-order-lines.client'
import {
  applyPrefill,
  type CreatorCityView,
  type CreatorOfferingView,
  type CreatorPrefill,
  needsRange,
  needsSource,
} from '@/lib/creator'
import { CONTAINER, PAGE_TOP } from '@/lib/layout'
import type { StoredLine } from '@/lib/order-line'
import { isReady, prefillTarget, pricedLines, viewLines } from '@/lib/order-line-view'
import { orderTotals } from '@/lib/order-money'
import { formatPLN, withVat } from '@/lib/tiers'
import { ACCENT_TEXT, META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * **The order screen: pick it, price it, and place it — all here.**
 *
 * One list of positions, and the server owns it. The basket used to live in
 * `localStorage` behind a React context in the root layout, which cost every
 * page on the site — the holding page included — a 301 kB chunk of `zod` it had no
 * use for, and cost this screen the ability to be one screen at all: the page
 * that knew what you were configuring and the page that held your basket were
 * different lists, and a position had to be "added" from one to the other.
 *
 * That step is gone. A line is in the order from the moment it exists, and the
 * completeness gate moved from the button to the field.
 *
 * A position renders from the catalogue's CAPABILITY COLUMNS and nothing else,
 * so a new kind of offer costs no code here. Two discounts stack and both are
 * shown as they are earned rather than at checkout: the per-line ladder and the
 * bonus for combining services.
 */
export function OrderPageView({
  locale,
  offerings,
  cities,
  prefill,
  initialNotice = null,
  initialLines,
  stored,
  signedIn,
  terms,
  rules = DEFAULT_PRICING_RULES,
}: {
  locale: string
  /** The whole active offer — see `loadCreatorCatalogue`. */
  offerings: CreatorOfferingView[]
  cities: CreatorCityView[]
  /** Read off the query by the route, so a link from an offer page arrives set up. */
  prefill: CreatorPrefill
  /** What the server swept out of the basket before this page rendered. */
  initialNotice?: string | null
  /** The basket as the server holds it. Empty for anybody who has not started one. */
  initialLines: StoredLine[]
  /** Whether a row exists at all — an emptied basket still has one to erase. */
  stored: boolean
  /** An order is a commitment to pay, and paying needs an account (spec §3). */
  signedIn: boolean
  /**
   * The pricing rules in force (Cennik → Reguły), read on the server.
   *
   * The basket has to quote the same footage rate and the same coordinator fee
   * the checkout is about to charge. Both used to be constants compiled into
   * the bundle, so retuning either in the panel would have left this screen
   * quoting the old figure for as long as the build lasted — which is to say,
   * until somebody noticed the order total disagreeing with the basket.
   */
  rules?: PricingRuleValues
  /**
   * The pre-order information block (u. o prawach konsumenta art. 12), rendered
   * on the SERVER by the route and passed through as a slot — it reads the
   * seller's identity and the gateway's configuration, neither of which belongs
   * in the client bundle.
   */
  terms: ReactNode
}) {
  const order = useOrderLines(initialLines, stored, initialNotice)
  /** Set once the order exists; replaces the whole grid. */
  const [placed, setPlaced] = useState<Placed | null>(null)
  /**
   * "Chcę koordynatora" (spec §13 · H3).
   *
   * Kept HERE rather than in the basket, because it is a fact about this
   * checkout and not about the lines: the threshold moves as positions are
   * added and removed, and a tick stored against a basket that has since
   * shrunk below two positions would be a charge nobody could see the reason
   * for. The server reaches the same verdict from the same count.
   */
  const [coordinator, setCoordinator] = useState(false)
  /**
   * How the equipment travels (spec §11 · I6).
   *
   * One choice for the whole basket, as spec §11 words it. Kept here beside
   * the coordinator tick for the same reason: it is a fact about this
   * checkout, not about any line, and the server reaches the same verdict from
   * the same basket.
   */
  const [delivery, setDelivery] = useState<DeliveryMethod>('PICKUP')

  const bySlug = useMemo(() => new Map(offerings.map((o) => [o.slug, o])), [offerings])

  const views = viewLines(order.lines, bySlug, rules)
  const ready = views.filter(isReady)

  /**
   * A line whose offering left the catalogue WHILE this page was open, dropped
   * without asking.
   *
   * The server sweeps these before the page paints (`pruneDeadCartLines`), so
   * this only catches the narrow race: an Admin withdraws something between the
   * render and whatever the visitor does next. Same reasoning either way —
   * nothing was agreed and nothing was paid, so it is a dead row and not a
   * chore to hand to the customer.
   *
   * `dropped` is a ref of ids already sent. Without it a failed delete would be
   * retried on every render, and React's development double-invoke would send
   * each one twice.
   */
  const dropped = useRef(new Set<string>())
  const dropLine = useRef(order.drop)
  dropLine.current = order.drop
  const orphanIds = views
    .filter((view) => view.status === 'orphan')
    .map((view) => view.line.lineId)
    .join(',')
  useEffect(() => {
    for (const lineId of orphanIds.split(',').filter(Boolean)) {
      if (dropped.current.has(lineId)) continue
      dropped.current.add(lineId)
      dropLine.current(lineId)
    }
    // The ids, joined, are the whole dependency: `order.drop` is rebuilt on
    // every render, so depending on it would fire this on every keystroke.
  }, [orphanIds])
  // Spec §7's declaration is asked for only when there is material to declare
  // anything about. A basket of thumbnails must not be shown a tick box about
  // footage nobody handed over — and `createOrder` reaches the same conclusion
  // from the same fact, so the screen and the server cannot disagree about
  // whether the order needs one.
  const needsMaterialConsent = ready.some((view) => needsSource(view.offering, view.line.pick))
  // Spec §11 · I6: the delivery choice and the rental-terms tick appear only
  // when there is gear to deliver. Asked of the READY lines, like the material
  // declaration above — a half-finished camera line has nothing to send yet.
  const hasRental = ready.some((view) => needsRange(view.offering))

  /**
   * The films in this basket — what a reel may be told to come from (spec §8 · G8).
   *
   * Every line priced by the minute that is not itself cut from something,
   * finished or not: a film still missing its length band will have one in a
   * moment, and hiding the option until then would make the two controls fight.
   * `dependencyProblem` refuses a chain on the server, so the filter here is the
   * screen agreeing rather than the screen deciding.
   */
  const films = views.flatMap((view) =>
    view.status !== 'orphan' &&
    view.offering.pricingUnit === 'MINUTE' &&
    view.line.pick.fromFilmLineId === null
      ? [
          {
            lineId: view.line.lineId,
            label: view.variant
              ? `${view.offering.name} — ${view.variant.name}`
              : view.offering.name,
          },
        ]
      : [],
  )
  // Every line with an AMOUNT, not every line that could be ordered. A half-
  // filled position still costs what its variant and quantity say it costs, and
  // leaving it out is how the summary came to sit still while a price appeared
  // on the card next to it. `ready` still decides whether the order may be
  // placed — see the submit button.
  const totals = orderTotals(pricedLines(views), coordinator, rules)

  /**
   * What a deep link asked for, if it has not already become a line.
   *
   * It is handed to the catalogue panel, which pins it first and points its
   * tile at the level the link named. Everything else on offer is in the same
   * grid — there is no separate "suggestions" list any more, because two ways
   * to add the same thing is what the old screen got wrong. See
   * `OrderCatalogue`.
   *
   * Still nothing is written until somebody clicks: the prefill is an
   * instruction for the basket, not a description of the screen.
   */
  const target = prefillTarget(prefill, order.lines, bySlug)
  const linkedOffering =
    target?.kind === 'append' && prefill.offeringSlug ? bySlug.get(prefill.offeringSlug) : undefined
  const linked = linkedOffering
    ? { offering: linkedOffering, pick: applyPrefill(linkedOffering, prefill) }
    : undefined
  const taken = new Set(order.lines.map((line) => line.offeringSlug))

  const unfinished = views.length - ready.length
  const blocked =
    unfinished > 0
      ? `Dokończ ${unfinished === 1 ? 'pozycję' : 'pozycje'} obok — bez tego nie złożymy zamówienia.`
      : null

  const gross = withVat(totals.total)

  return (
    <PricingRulesProvider rules={rules}>
      <div className="relative w-full overflow-x-hidden">
        {/* `CONTAINER`, like the header above it and the footer below it. This
          screen was the last one holding its own `max-w-7xl px-6 md:px-10`,
          which is 80rem inside a site framed at 110rem: at 1920 the bar ran
          104 → 1816 and the checkout under it ran 360 → 1560, so the page a
          person pays on sat 256px inside the page they arrived from. */}
        <div className={cn(CONTAINER, PAGE_TOP, 'relative z-10')}>
          <PageHeader>
            Sam decydujesz. <span className={ACCENT_TEXT}>Więcej zyskujesz.</span>
          </PageHeader>

          {placed ? (
            <div className="mt-12">
              <SuccessPanel locale={locale} placed={placed} />
            </div>
          ) : (
            // The rail widens at `2xl` and NOT at `xl`, which is the difference
            // between using the new room and spending room the page does not
            // have yet. `CONTAINER` caps at 110rem but is fluid below it, so at
            // 1280 the frame gains 32px over the old one — widening the rail by
            // 64px there would have taken 32px OFF the positions, at the commonest
            // desktop width, to fix a problem that only exists above 1536.
            <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_24rem] 2xl:grid-cols-[minmax(0,1fr)_28rem] 2xl:gap-8">
              <div className="flex flex-col gap-10">
                <OrderLineList views={views} cities={cities} films={films} order={order} />

                <OrderCatalogue
                  offerings={offerings}
                  taken={taken}
                  {...(linked ? { linked } : {})}
                  hasLines={views.length > 0}
                  onAdd={order.add}
                />
              </div>

              {/* One column, two cards, no sticky. The summary was the obvious
                candidate to pin, but with the form stacked under it in the same
                column a sticky summary sits on top of the fields as they scroll
                up. The rail moves as one; the phone gets the bar below instead. */}
              <div className="flex flex-col gap-6">
                <OrderTotalCard
                  totals={totals}
                  hasLines={views.length > 0}
                  coordinator={coordinator}
                  onCoordinator={setCoordinator}
                  onClear={order.clear}
                />
                <OrderContactCard
                  id="dane"
                  locale={locale}
                  signedIn={signedIn}
                  // The basket's size, NOT how much of it is finished. They used
                  // to be the same number, so a basket of two half-filled
                  // positions reported itself EMPTY: the button read "Dodaj
                  // pozycję do zamówienia" and the "dokończ pozycje obok" line
                  // was suppressed as irrelevant to an empty basket. `blocked` is
                  // what refuses an unfinished order, and it still does.
                  lineCount={views.length}
                  coordinator={coordinator}
                  hasRental={hasRental}
                  delivery={delivery}
                  setDelivery={setDelivery}
                  needsMaterialConsent={needsMaterialConsent}
                  blocked={blocked}
                  terms={terms}
                  flushLines={order.flush}
                  onPlaced={(result) => {
                    setPlaced(result)
                    order.reset()
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* The amount, kept in sight on a phone — where the rail is three screens
          below the positions. `env(safe-area-inset-bottom)` so it clears the
          home indicator instead of sitting under it. */}
        {!placed && totals.total > 0 ? (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-black/80 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-xl lg:hidden">
            <div className={cn(CONTAINER, 'flex items-center justify-between gap-4')}>
              <div>
                <span className={cn(META, 'block text-white/55')}>Do zapłaty</span>
                <span className="text-[1.15rem] leading-tight font-bold text-white">
                  {formatPLN(gross)}
                </span>
              </div>
              <a
                href="#dane"
                className="inline-flex min-h-11 items-center rounded-full bg-white px-5 text-[0.85rem] font-semibold text-neutral-900"
              >
                Przejdź do danych
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </PricingRulesProvider>
  )
}
